import { Component, inject, input } from "@angular/core";
import { AlertService, InlineInput } from "@ui";
import { type ColumnMeta, type Corpus, maxColumnNameLength } from "@pienso/ingest-types/corpus.interface";
import { CorpusDataService } from "../services/corpus-data.service";
import { columnTypeDisplayNames } from "../services/elastic-search.service";
import type { Page } from "./grid-data.provider";
import type { HeaderRendererColumn } from "./header-renderer.interface";

/** Handles column name changes if set to editable */
@Component({
  template: `
    <div class="datatable-header-cell-wrapper p-flex-col-xxs p-flex-start p-w-full">
      @if (editable()) {
      <p-inline-input [text]="column().name ?? ''" (textChange)="changeColumnName($event)" />
      } @else {
      <span class="datatable-header-cell-label">{{ column().name || '\u00a0' }}</span>
      }
      @if (columnTypeDisplayName(); as columnType) {
      <span class="p-text-footnote p-color-text-disabled">{{ columnType }}</span>
      }
    </div>
    `,
  imports: [InlineInput],
})
export class DefaultHeaderRendererComponent {
  private corpusService = inject(CorpusDataService);
  private alertService = inject(AlertService);

  readonly corpus = input.required<Corpus>();
  readonly column = input.required<HeaderRendererColumn>();
  readonly editable = input(true);
  readonly page = input<Page>();

  readonly maxColumnNameLength = maxColumnNameLength;

  changeColumnName(name: string) {
    name = name.slice(0, maxColumnNameLength);
    const corpus = this.corpus();
    const column = this.column();
    if (name === column.name) {
      return;
    }
    this.corpusService.renameColumn(corpus.id, column.prop, name).subscribe({
      next: () => {
        column.name = name;
        (corpus.columnMeta as ColumnMeta).columns[column.prop].name = name;
      },
      error: (e) => this.alertService.error(e),
    });
  }

  columnTypeDisplayName(): string | null {
    const type = this.column().dataType?.type;
    return type ? (columnTypeDisplayNames[type] ?? type) : null;
  }
}
